import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { CatHeading } from "blocks-nextjs/blocks-react/components/Heading/Heading";
import { CatLoadingIndicator } from "blocks-nextjs/blocks-react/components/LoadingIndicator/LoadingIndicator";
import React, { useCallback, useMemo } from "react";
import { useChargerHistoryQuery } from "../../services/query/eflow-charger-dashboard-api.query";
import useProjectDetailsStore from "../../store/useProjectDetailsStore";
import { formatDateWithTimezoneOffset } from "../../utils/dateFormatter";
import DonutChart from "../Charts/DonutChart/DonutChart";
const ChargeHistoryTotal = ({ assetId, fromDate, projectId, toDate }) => {
    // Get timezone offset from project metadata
    const { projectMetaData } = useProjectDetailsStore((state) => ({
        projectMetaData: state.projectMetaData
    }));
    const timezoneOffset = useMemo(() => {
        return projectMetaData?.offset || "UTC+00:00";
    }, [projectMetaData?.offset]);
    // Use today's date for both from and to if not provided
    const today = useMemo(() => {
        const now = new Date();
        // Set to start of day (00:00:00)
        now.setHours(0, 0, 0, 0);
        return now;
    }, []);
    const calculatedFromDate = fromDate || today;
    const calculatedToDate = toDate || today;
    // Memoize query parameters with timezone-aware formatted dates
    const historyQueryParams = useMemo(() => {
        const formattedFromDate = formatDateWithTimezoneOffset(calculatedFromDate, timezoneOffset);
        const formattedToDate = formatDateWithTimezoneOffset(calculatedToDate, timezoneOffset);
        return {
            fromTime: formattedFromDate,
            toTime: formattedToDate
        };
    }, [calculatedFromDate, calculatedToDate, timezoneOffset]);
    // Fetch charger history - only enabled when assetId and projectId are available
    // Query will automatically refetch when historyQueryParams change
    const { data: historyData, isError, isLoading } = useChargerHistoryQuery(assetId || "", projectId || "", historyQueryParams, !!assetId && !!projectId);
    // Show loading if assetId or projectId is not available yet
    const isWaitingForChargerId = !assetId || !projectId;
    // Extract status data from API response
    // Note: TypeScript types define connectorStatusHistoryDetails as an array, but the API actually returns an object
    // Using type assertion to match actual API behavior
    const connectorDetails = historyData?.connectorStatusHistoryDetails;
    const statuses = connectorDetails?.connectorStatusHistory;
    // Create status entries dynamically from API response
    const statusEntries = useMemo(() => {
        if (!statuses)
            return [];
        // Map of status keys to display labels and colors (handle both case variations)
        const statusConfig = {
            available: { color: "#0066FF", label: "Available" },
            charging: { color: "#1AC69E", label: "Charging" },
            faulted: { color: "#FB5A00", label: "Faulted" },
            finishing: { color: "#9370DB", label: "Finishing" },
            offline: { color: "#E1E1E1", label: "Offline" },
            preparing: { color: "#FFA500", label: "Preparing" },
            reserved: { color: "#FFD700", label: "Reserved" },
            suspendedEV: { color: "#FF6347", label: "Suspended EV" }, // API uses capital EV
            suspendedEv: { color: "#FF6347", label: "Suspended EV" },
            suspendedEvse: { color: "#FF4500", label: "Suspended EVSE" },
            unavailable: { color: "#A9A9A9", label: "Unavailable" },
            unknown: { color: "#808080", label: "Unknown" }
        };
        // Only show these specific statuses
        const allowedStatuses = ["charging", "available", "faulted", "offline"];
        return Object.entries(statuses)
            .filter(([key, value]) => {
            // Only include allowed statuses
            if (!allowedStatuses.includes(key.toLowerCase()))
                return false;
            // Exclude lastReportedTimestamp (string type) and focus on status objects
            if (key === "lastReportedTimestamp" || typeof value === "string")
                return false;
            // Only include statuses with valid value property
            const hasValue = value && typeof value === "object" && "value" in value;
            const valueNum = hasValue ? value.value : undefined;
            return hasValue && typeof valueNum === "number";
        })
            .map(([key, value]) => {
            const minutes = value.value;
            const hours = minutes / 60; // Convert minutes to hours inline
            return {
                color: statusConfig[key]?.color || "#808080",
                hours,
                key,
                label: statusConfig[key]?.label || key.charAt(0).toUpperCase() + key.slice(1),
                minutes
            };
        });
    }, [statuses]);
    const hasData = statusEntries.length > 0;
    // Format hours for display - memoized to prevent recreation on every render
    const formatHours = useCallback((hours) => {
        const wholeHours = Math.floor(hours);
        const minutes = Math.round((hours - wholeHours) * 60);
        // If both are 0, show only "0hrs"
        if (wholeHours === 0 && minutes === 0) {
            return "0hrs";
        }
        // If minutes are 0, show only hours
        if (minutes === 0) {
            return `${wholeHours}hrs`;
        }
        // Otherwise show both with "hrs" and "min"
        return `${wholeHours}hrs ${minutes}min`;
    }, []);
    return (_jsxs("div", { className: "border-solid border-2 rounded p-4 flex-grow", children: [_jsx("div", { className: "flex items-center table-title", slot: "header", children: _jsx(CatHeading, { "data-test": "asset-page-details-header-history", variant: "title", children: "Charge History Total" }) }), _jsx("div", { className: "flex flex-col sm:flex-row", children: isWaitingForChargerId || isLoading ? (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", "data-testid": "loading-indicator", children: _jsx(CatLoadingIndicator, {}) })) : isError ? (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", children: "Error loading data" })) : hasData ? (_jsxs(_Fragment, { children: [_jsx("div", { className: "flex flex-start justify-center sm:justify-start", "data-testid": "asset-page-details-header-chart", children: _jsx(DonutChart, { colors: statusEntries.map((entry) => entry.color), height: 200, innerRadius: 0.8, labels: statusEntries.map((entry) => entry.label), showLegend: false, values: statusEntries.map((entry) => entry.hours), width: 200 }) }), _jsx("div", { className: "w-full", children: _jsxs("table", { className: "table-auto w-full text-left", "data-test": "asset-page-details-table", children: [_jsx("thead", { className: "py-4", children: _jsxs("tr", { className: "border-b", children: [_jsx("th", { "data-test": "asset-page-details-header-activity", children: "Status" }), _jsx("th", { className: "text-right", "data-test": "asset-page-details-header-activity", children: "Power Used" })] }) }), _jsx("tbody", { "data-test": "asset-page-details-power-table", children: statusEntries.map((entry, index) => (_jsxs("tr", { className: index < statusEntries.length - 1 ? "border-b" : "", children: [_jsx("td", { className: "py-2", children: entry.label }), _jsx("td", { className: "py-2 text-right", children: formatHours(entry.hours) })] }, entry.key))) })] }) }, "charger-history-table")] })) : (_jsx("div", { className: "flex justify-center items-center h-full w-full min-h-[20vh]", children: "No data" })) }), hasData && (_jsxs("div", { className: "flex w-full items-center justify-center mt-5", children: [_jsx("div", { className: "text-md mr-2", children: "Legend:" }), _jsx("span", { className: "flex flex-wrap text-md", children: statusEntries.map((entry) => (_jsxs("div", { className: "flex items-center mr-4 whitespace-nowrap", children: [_jsx("div", { className: "w-2.5 h-2.5 rounded-full mr-1", style: { backgroundColor: entry.color } }), _jsx("span", { children: entry.label })] }, `legend-${entry.key}`))) })] }))] }));
};
export default ChargeHistoryTotal;
